'use client';

import React from 'react';
import { twJoin } from 'tailwind-merge';
import {
  ImageContentFragment,
  PieceFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { buildTrackingId } from '@/utils/tracking-helper';
import { VideoSource } from '@/utils/video-source';
import { useHoverCapable } from '@/ui/hooks/use-hover-capable';
import { VideoPlayer } from '@/ui/components/shared/video-player/video-player';
import { getVideoAriaLabel, parseVideoSeo } from '@/utils/video-seo';
import {
  getVideoAspectRatio,
  getVideoMaxWidth,
} from '@/ui/components/shared/video-player/video-sizing';

export interface VideoText5050MediaProps {
  source: VideoSource;
  poster?: ImageContentFragment | null;
  videoSeo?: PieceFragment | null;
  titleText?: string;
  aspectRatio?: string;
  maxWidth?: number;
  maxHeight?: number;
  isVideoRight?: boolean;
  trackingPrefix?: string;
}

export function VideoText5050Media({
  source,
  poster,
  videoSeo,
  titleText,
  aspectRatio,
  maxWidth,
  maxHeight,
  isVideoRight,
  trackingPrefix,
}: Readonly<VideoText5050MediaProps>) {
  const isHoverCapable = useHoverCapable();

  const posterImage = poster?.images?.[0];
  const seo = parseVideoSeo(videoSeo);
  const ariaLabel = getVideoAriaLabel(seo, posterImage?.altText, titleText);
  // Hover capability is unknown on the first render — the click affordance is the safe default.
  const isTouchDevice = isHoverCapable === false;
  const { className: aspectRatioClass, ratio } = getVideoAspectRatio(aspectRatio, '16:9');

  return (
    <div
      data-testid="video-text-5050-media"
      className={twJoin(
        'relative mx-auto mb-6 w-full overflow-hidden rounded-xl md:my-auto',
        aspectRatioClass,
        isVideoRight && 'md:order-2',
      )}
      style={{ maxWidth: getVideoMaxWidth(maxWidth, maxHeight, ratio) }}
    >
      <VideoPlayer
        source={source}
        poster={posterImage}
        ariaLabel={ariaLabel}
        mode={isTouchDevice ? 'click' : 'hover'}
        loop
        showControls
        className="absolute inset-0 h-full w-full"
        trackingId={buildTrackingId(trackingPrefix, 'video')}
      />
    </div>
  );
}
